import { Injectable } from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormGroup,
  AbstractControlOptions,
  Validators,
} from '@angular/forms';
import { PrFormAbstract } from '@app/components/pr-form/pr-form.abstract';
import { PledgeProductRead } from '@app/forms/subscription/definition/interface/Pledge';
import { FormConfig } from '@app/forms/subscription/definition/types/subscription';
import FormGroupArray from '@app/forms/subscription/services/FormGroupArray';
import * as _ from 'lodash';
import { Observable, takeUntil } from 'rxjs';
import {
  allocateAmounts,
  clamp,
  FULL_AMOUNT_TOLERANCE,
  maxFractionDigits,
  roundTo,
  toNumber,
} from '../services/versement-allocation';

@Injectable({
  providedIn: 'root',
})
export abstract class EaSubscriptionBuilder extends PrFormAbstract {
  private synced = new WeakSet<UntypedFormGroup>();
  private redistributing = false;

  generateForm(entity: PledgeProductRead & { parameters: any }, option) {
    let options: AbstractControlOptions = {};

    let supportRepartition: UntypedFormGroup[] = [];

    const entitySupportRepartition =
      entity?.parameters?.supportsInvestment?.supportRepartition || [];

    if (entitySupportRepartition)
      _.forEach(entitySupportRepartition, (support, i) =>
        supportRepartition.push(
          this.formBuilder.group(this.createModel(support)),
        ),
      );

    const formConfig: FormConfig<PledgeProductRead & { parameters: any }> = {
      id: [entity.id],
      type: [entity.type],
      productId: [entity.productId],
      productName: [entity.productName],
      status: [entity.status],
      parameters: this.formBuilder.group({
        avProgammedPaymentType: [
          entity?.parameters?.avProgammedPaymentType || 'MONTHLY',
        ],
        totalAmount: [
          entity?.parameters?.supportsInvestment?.totalAmount || null,
        ],
        supportRepartition: this.formBuilder.array(
          supportRepartition,
          Validators.required,
        ),
      }),
    };

    return this.formBuilder.group(formConfig, options);
  }
  generate(entity: any, option) {
    return FormGroupArray.generate({
      entity,
      options: option,
      formBuilder: this.formBuilder,
      generateForm: this.generateForm.bind(this),
      propertyName: 'products',
    });
  }

  public createModel(item) {
    return {
      supportId: [item ? item.supportId : null],
      supportName: [item ? item.supportName : null],
      name: [item ? item.supportName || item.name : null],
      isin: [item ? item.isin : null],
      isr: [item ? item.isr : null],
      amount: [
        item && !_.isNil(item.amount) ? roundTo(item.amount, 2) : 0,
        [Validators.required, Validators.min(0)],
      ],
      percentage: [
        item && !_.isNil(item.percentage)
          ? roundTo(item.percentage * 100, 4)
          : 0,
        [
          Validators.required,
          Validators.min(0),
          Validators.max(100),
          maxFractionDigits(4),
        ],
      ],
      supportType: [item ? item.supportType : null],
      index: [item?.index || null],
      perfP3Y: [item?.perfP3Y || null],
      volat3Y: [item?.volat3Y || null],
      risk: [item?.risk || null],
      // PRE-4896 : cette slide n'affiche ni ne modifie ces 8 champs, mais doit les faire transiter
      // tels quels (préremplis par la commande COMPLETE_SUPPORT_RATES au submit de cette slide) —
      // sans cela, l'extract() écraserait leur valeur à null avant même que la slide
      // "tauxParSupportVersement" ait pu les afficher.
      upfrontAssureurAutocomplete: [item?.upfrontAssureurAutocomplete ?? null],
      upfrontEmetteurAutocomplete: [item?.upfrontEmetteurAutocomplete ?? null],
      fraisGestionAutocomplete: [item?.fraisGestionAutocomplete ?? null],
      retrocessionAssureurAutocomplete: [
        item?.retrocessionAssureurAutocomplete ?? null,
      ],
      upfrontAssureurSaisi: [item?.upfrontAssureurSaisi ?? null],
      upfrontEmetteurSaisi: [item?.upfrontEmetteurSaisi ?? null],
      fraisGestionSaisi: [item?.fraisGestionSaisi ?? null],
      retrocessionAssureurSaisi: [item?.retrocessionAssureurSaisi ?? null],
    };
  }

  attachVersementSync(destroy$: Observable<void>) {
    const rows = this.supportRepartition;
    if (!rows) return;

    const total = () => this.versementAmount;
    if (!this.hasVersementAmount) return;

    const attachOne = (row: UntypedFormGroup) => {
      if (this.synced.has(row)) return;
      this.synced.add(row);

      const percentControl = row.get('percentage');
      const amountControl = row.get('amount');
      if (!percentControl || !amountControl) return;

      let syncing = false;

      percentControl.valueChanges.pipe(takeUntil(destroy$)).subscribe(raw => {
        if (syncing) return;
        syncing = true;
        try {
          const percent = roundTo(clamp(toNumber(raw), 0, 100), 4);
          if (percent !== Number(raw)) {
            percentControl.setValue(percent, { emitEvent: false });
          }
          this.recomputeAmountsFromPercents(rows, total());
        } finally {
          syncing = false;
        }
      });

      amountControl.valueChanges.pipe(takeUntil(destroy$)).subscribe(raw => {
        if (syncing) return;
        syncing = true;
        try {
          const available = total();
          const amount = roundTo(clamp(toNumber(raw), 0, available), 2);
          if (amount !== Number(raw)) {
            amountControl.setValue(amount, { emitEvent: false });
          }
          const percent = roundTo(
            clamp(available > 0 ? (amount / available) * 100 : 0, 0, 100),
            4,
          );
          percentControl.setValue(percent, { emitEvent: false });
          percentControl.updateValueAndValidity({ emitEvent: false });
        } finally {
          syncing = false;
        }
      });
    };

    rows.controls.forEach(row => attachOne(row as UntypedFormGroup));

    rows.valueChanges
      .pipe(takeUntil(destroy$))
      .subscribe(() =>
        rows.controls.forEach(row => attachOne(row as UntypedFormGroup)),
      );

    this.recomputeAmountsFromPercents(rows, total());
  }

  private recomputeAmountsFromPercents(
    rows: UntypedFormArray,
    totalEuros: number,
  ) {
    if (this.redistributing) return;
    this.redistributing = true;
    try {
      const percents = rows.controls.map(row =>
        toNumber(row.get('percentage')?.value),
      );
      const amounts = allocateAmounts(percents, totalEuros);

      rows.controls.forEach((row, index) => {
        const amountControl = row.get('amount');
        if (!amountControl) return;
        if (toNumber(amountControl.value) !== amounts[index]) {
          amountControl.setValue(amounts[index], { emitEvent: false });
        }
      });
    } finally {
      this.redistributing = false;
    }
  }

  get products() {
    return this.form.get('products') as UntypedFormArray;
  }
  get supportRepartition() {
    return this.products
      .get(`${0}`)
      .get('parameters')
      .get('supportRepartition') as UntypedFormArray;
  }

  get productId() {
    return this.form.get('products').get(`${0}`).get('productId').value;
  }

  get versementAmount(): number {
    return toNumber(
      this.products.get(`${0}`).get('parameters').get('totalAmount')?.value,
    );
  }

  get isVersementOperation(): boolean {
    return ['VERSEMENT_COMPLEMENTAIRE', 'VERSEMENT_PROGRAMME'].includes(
      this.entity?.operation,
    );
  }

  get hasVersementAmount(): boolean {
    return this.isVersementOperation && this.versementAmount > 0;
  }

  get totalRepartitionPercent(): number {
    const rows = this.supportRepartition;
    if (!rows) return 0;
    return rows.controls.reduce(
      (sum, row) => sum + toNumber(row.get('percentage')?.value),
      0,
    );
  }

  get remainingPercent(): number {
    return roundTo(100 - this.totalRepartitionPercent, 4);
  }

  get totalRepartitionAmount(): number {
    const rows = this.supportRepartition;
    if (!rows) return 0;
    return rows.controls.reduce(
      (sum, row) => sum + toNumber(row.get('amount')?.value),
      0,
    );
  }

  get remainingAmount(): number {
    return roundTo(this.versementAmount - this.totalRepartitionAmount, 2);
  }

  get isFullyAllocated(): boolean {
    return Math.abs(this.remainingAmount) < FULL_AMOUNT_TOLERANCE;
  }

  extract() {
    const products = _.cloneDeep(this.form.value.products);
    products.forEach(product => {
      const supportRepartition = (
        product.parameters.supportRepartition || []
      ).map(support => ({
        ..._.omit(support, ['name']),
        amount: roundTo(support.amount, 2),
        percentage: roundTo(toNumber(support.percentage) / 100, 6),
      }));

      const target = this.isVersementOperation
        ? toNumber(product.parameters.totalAmount)
        : 0;
      const allocated = supportRepartition.reduce(
        (acc, item) => acc + toNumber(item.amount),
        0,
      );

      product.parameters.totalAmount = undefined;
      product.parameters.supportRepartition = supportRepartition;
      product.parameters.supportsInvestment = {
        totalAmount: roundTo(target > 0 ? target : allocated, 2),
        supportRepartition,
      };
    });
    return products;
  }
}
